import React, { useEffect, useState } from 'react'
import ArtworkBackgroundControl from './ArtworkBackgroundControl'
import ArtworkSizeControls from './ArtworkSizeControls'
import ArtworkQuantityInput from './ArtworkQuantityInput'
import DpiBadge from './DpiBadge'
import { deriveDefaultArtworkDimensions, effectiveRequestedQuantity, normalizeArtworkConfig, MAX_ARTWORK_QUANTITY } from './artworkSource'
import { PROCESSING_ERRORS } from './processingErrors'

export default function ArtworkSourceCard({ image, imageUrl, config, placedCount, sheetCount, sheetNumber, expanded,
  onToggle, onDragStart, onPlace, onChange, onRemove, onRetry, deleting, disabled, sheetSize,
  onBackgroundPreview, onBackgroundApply, hasSelectedCopy, children }) {
  const [confirmDelete, setConfirmDelete] = useState(false)
  const normalized = normalizeArtworkConfig(config, image)
  const quantity = effectiveRequestedQuantity(normalized)
  const processed = image.processingStatus === 'processed' || image.processingStatus === undefined
  const failed = image.processingStatus === 'failed'
  const canEdit = processed && !disabled && !deleting
  const defaultDimensions = deriveDefaultArtworkDimensions({ ...image, ...normalized })
  useEffect(() => {
    if (defaultDimensions) onChange?.(image.id, { ...normalized, ...defaultDimensions })
  }, [image.id, defaultDimensions?.printWidthIn, defaultDimensions?.printHeightIn]) // Source defaults only when dimensions first become available.

  const update = changes => onChange?.(image.id, normalizeArtworkConfig({ ...normalized, ...changes }, image))
  return (
    <article className={`artwork-card artwork-card--source${expanded ? ' artwork-card--expanded' : ''}${failed ? ' artwork-card--failed' : ''}`}
      data-testid={`graphics-item-${image.id}`}>
      <div className="artwork-card__summary">
        {imageUrl ? <img src={imageUrl} crossOrigin="anonymous" alt={image.originalFilename} className="artwork-card__thumb" data-testid={`sidebar-thumb-${image.id}`} />
          : <div className="artwork-card__thumb artwork-card__thumb--empty" aria-hidden="true" />}
        <div className="artwork-card__meta">
          <button type="button" className="artwork-source-toggle" aria-expanded={expanded}
            aria-controls={`artwork-card-controls-${image.id}`} onClick={onToggle}>
            <span className="artwork-card__name" title={image.originalFilename}>{image.originalFilename}</span>
            <span aria-hidden="true">{expanded ? '▾' : '▸'}</span>
          </button>
          {image.byteSize > 0 && <span className="artwork-card__file-size">{(image.byteSize / (1024 * 1024)).toFixed(1)} MB</span>}
          <span className="artwork-card__qty" data-testid={`artwork-counts-${image.id}`}>
            {quantity} requested · {placedCount} placed
          </span>
          <span className="artwork-card__qty">{sheetCount} on Sheet {sheetNumber}</span>
          <div className="artwork-card__badges">
            {!hasSelectedCopy && normalized.printWidthIn > 0 && normalized.printHeightIn > 0 && (
              <span className="artwork-card__size">Size when adding: {normalized.printWidthIn}&quot; × {normalized.printHeightIn}&quot;</span>
            )}
            {processed && !hasSelectedCopy && <DpiBadge widthPx={image.widthPx} heightPx={image.heightPx} printWidthIn={normalized.printWidthIn} printHeightIn={normalized.printHeightIn} />}
            {!processed && <span className="artwork-card__status-badge" data-testid={`status-badge-${image.id}`}>{failed ? 'Failed' : 'Processing...'}</span>}
          </div>
          {processed && quantity !== placedCount && <span className="artwork-card__mismatch">Use Auto Arrange to match quantity</span>}
          {!expanded && <button type="button" className="artwork-card__edit-cue" data-testid={`artwork-card-edit-cue-${image.id}`} onClick={onToggle}>Click to edit size &amp; quantity</button>}
        </div>
      </div>
      {expanded && (
        <div className="artwork-card__details" id={`artwork-card-controls-${image.id}`} data-testid={`artwork-card-controls-${image.id}`}>
          {failed ? (
            <>
              <p className="artwork-card__error">{PROCESSING_ERRORS[image.processingError] || PROCESSING_ERRORS.processing_error}</p>
              {onRetry && <div className="artwork-card__actions"><button type="button" onClick={() => onRetry(image)}>Re-upload</button></div>}
            </>
          ) : (
            <>
              {children}
              <label className="artwork-source-quantity">
                <span>Requested total · all sheets</span>
                <ArtworkQuantityInput value={quantity} disabled={!canEdit}
                  aria-label={`${image.originalFilename} quantity`} data-testid={`artwork-card-quantity-${image.id}`}
                  aria-describedby={`artwork-quantity-help-${image.id}`}
                  onCommit={quantity => update({ quantity, included: quantity > 0 })} />
              </label>
              <p className="graphics-panel__helper" id={`artwork-quantity-help-${image.id}`}>Enter 0–{MAX_ARTWORK_QUANTITY}. Press Enter or leave the field to apply.</p>
              {!hasSelectedCopy && <section className="artwork-source-size" aria-label={`Size when adding ${image.originalFilename} to sheet`}>
                <strong>Size when adding to sheet</strong>
                <p className="graphics-panel__helper">Used by Add to sheet, Drag to sheet, and additional copies from Auto Arrange. Existing copies keep their sizes; select one on the sheet to resize it.</p>
                <ArtworkSizeControls id={String(image.id)} image={image} width={normalized.printWidthIn} height={normalized.printHeightIn}
                  sheetSize={sheetSize} disabled={!canEdit} onChange={update} />
                <div className="artwork-card__actions">
                  <button type="button" disabled={!canEdit || placedCount >= MAX_ARTWORK_QUANTITY} onClick={onPlace}>Add to sheet</button>
                  <button type="button" disabled={!canEdit || placedCount >= MAX_ARTWORK_QUANTITY}
                    draggable={canEdit && placedCount < MAX_ARTWORK_QUANTITY} onDragStart={onDragStart}>Drag to sheet</button>
                </div>
              </section>}
              <ArtworkBackgroundControl image={image} onPreview={onBackgroundPreview} onApply={onBackgroundApply} />
            </>
          )}
          {onRemove && <div className="artwork-card__actions">
            {confirmDelete ? <div className="artwork-card__delete-confirmation" role="group" aria-label={`Confirm deletion of ${image.originalFilename}`}>
              <span>Delete this artwork and every copy?</span>
              <button type="button" disabled={deleting || disabled} onClick={() => { setConfirmDelete(false); onRemove(image.id) }}>{deleting ? 'Deleting…' : 'Confirm delete'}</button>
              <button type="button" disabled={deleting} onClick={() => setConfirmDelete(false)}>Cancel</button>
            </div> : <button type="button" disabled={deleting || disabled} data-testid={`artwork-card-delete-${image.id}`} onClick={() => setConfirmDelete(true)}>
              {deleting ? 'Deleting…' : placedCount > 0 ? 'Delete artwork + all copies' : 'Delete uploaded artwork'}
            </button>}
          </div>}
        </div>
      )}
    </article>
  )
}
